'use client';

import { motion } from 'motion/react';
import { usePathname, useRouter } from 'next/navigation';
import {
    MouseEvent,
    RefObject,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    useTransition,
} from 'react';
import { createPortal } from 'react-dom';
import { IntlShape, useIntl } from 'react-intl';
import { RemoveScroll } from 'react-remove-scroll';

import { useLazyExperimentVariation } from '../../core/Tracking/Amplitude/Amplitude';
import { DestinationsMenuTopDestination } from '../../resources/destinationsMenu/domain/types/destinationsMenu';
import { buildSearchSuggestionUrl } from '../../resources/searchSuggestions/domain/operations/buildSearchSuggestionUrl';
import { usePrefersReducedMotion } from '../../shared/hooks/usePrefersReducedMotion';
import { useUrlGenerator } from '../../shared/hooks/useUrlGenerator';

import { SearchInput } from './components/SearchInput';
import { SearchResults } from './components/SearchResults';
import { useFocusTrap } from './hooks/useFocusTrap';
import { useSearchKeyboard } from './hooks/useSearchKeyboard';
import { SearchQuerySuggestion, useSearchQuery } from './hooks/useSearchQuery';
import { destinationTypeFromSuggestion, useSearchTracking } from './hooks/useSearchTracking';
import style from './SearchOverlay.module.css';
import { SearchOverlayOrigin, SearchSubmissionTarget } from './SearchOverlay.types';
import { buildEmptyFallbackRows, buildNoResultsFallbackRows } from './utils/fallbackRows';

export type { SearchOverlayOrigin };

type Props = {
    onClose: () => void;
    onNavigationComplete?: () => void;
    origin: SearchOverlayOrigin;
    topDestinations?: DestinationsMenuTopDestination[];
    triggerRef?: RefObject<HTMLElement | null>;
};

export function SearchOverlay(props: Props) {
    return <SearchOverlayContent {...props} />;
}

function SearchOverlayContent({
    onClose,
    onNavigationComplete,
    origin,
    topDestinations,
    triggerRef,
}: Props) {
    const [searchQuery, setSearchQuery] = useState('');
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const router = useRouter();
    const { trackSearchbarTyped, trackSearchbarSubmitted, trackSearchbarClosed } =
        useSearchTracking(origin);
    const getSearchRecoVariant = useLazyExperimentVariation('fr_hp_search_reco');
    const containerRef = useRef<HTMLDivElement>(null);
    const reducedMotion = usePrefersReducedMotion();
    const listboxId = useId();
    const getOptionId = useCallback((index: number) => `${listboxId}-option-${index}`, [listboxId]);

    const state = useSearchQuery(searchQuery);
    const debouncedQuery = state.kind === 'empty' ? '' : state.query;

    const pathname = usePathname();
    const [isPending, startTransition] = useTransition();
    const [pendingIndex, setPendingIndex] = useState<number | null>(null);
    const isCancelledRef = useRef(false);
    const navStartPathnameRef = useRef<string | null>(null);
    const onNavigationCompleteRef = useRef(onNavigationComplete);
    onNavigationCompleteRef.current = onNavigationComplete;

    useEffect(() => {
        if (
            navStartPathnameRef.current === null ||
            isCancelledRef.current ||
            isPending ||
            pathname === navStartPathnameRef.current
        )
            return;
        navStartPathnameRef.current = null;
        setPendingIndex(null);
        onNavigationCompleteRef.current?.();
    }, [pathname, isPending]);

    const handleClose = useCallback(() => {
        trackSearchbarClosed();
        isCancelledRef.current = true;
        triggerRef?.current?.focus();
        onClose();
    }, [onClose, trackSearchbarClosed, triggerRef]);

    const buildSuggestionTarget = useCallback(
        (suggestion: SearchQuerySuggestion): SearchSubmissionTarget => {
            const variantHref =
                suggestion.type === 'destination' && getSearchRecoVariant()?.value === 'variation'
                    ? generatePath('suggestions', { destinationSlug: suggestion.slug })
                    : null;
            const href =
                variantHref ??
                buildSearchSuggestionUrl(suggestion, generatePath) ??
                generatePath('destination_list');
            return {
                amplitudeDestinationType: destinationTypeFromSuggestion(suggestion.type),
                label: suggestion.name,
                href,
            };
        },
        [generatePath, getSearchRecoVariant],
    );

    const emptyFallbackRows = useMemo(
        () =>
            topDestinations && topDestinations.length > 0
                ? buildEmptyFallbackRows(topDestinations, formatMessage, generatePath)
                : [],
        [topDestinations, formatMessage, generatePath],
    );

    const noResultsFallbackRows = useMemo(
        () => buildNoResultsFallbackRows(topDestinations, formatMessage, generatePath),
        [topDestinations, formatMessage, generatePath],
    );

    const resolveRowTarget = useCallback(
        (index: number): SearchSubmissionTarget | null => {
            switch (state.kind) {
                case 'results': {
                    const entry = resolveRenderableSuggestions(state.suggestions, generatePath)[
                        index
                    ];
                    return entry ? buildSuggestionTarget(entry.suggestion) : null;
                }
                case 'empty':
                    return emptyFallbackRows[index]?.target ?? null;
                case 'no-results':
                    return noResultsFallbackRows[index]?.target ?? null;
                case 'loading':
                case 'error':
                    return null;
            }
        },
        [state, generatePath, buildSuggestionTarget, emptyFallbackRows, noResultsFallbackRows],
    );

    const handleSelect = useCallback(
        (target: SearchSubmissionTarget, via: 'click' | 'keyboard', selectedIndex: number) => {
            if (
                target.amplitudeDestinationType === 'country' ||
                target.amplitudeDestinationType === 'coup de coeur'
            ) {
                getSearchRecoVariant();
            }
            trackSearchbarSubmitted({
                searchValue: debouncedQuery,
                label: target.label,
                amplitudeDestinationType: target.amplitudeDestinationType,
                scrollingMenuClicked: via === 'click',
            });
            isCancelledRef.current = false;
            navStartPathnameRef.current = pathname;
            setPendingIndex(selectedIndex);
            startTransition(() => {
                router.push(target.href);
            });
        },
        [
            debouncedQuery,
            getSearchRecoVariant,
            pathname,
            router,
            startTransition,
            trackSearchbarSubmitted,
        ],
    );

    const itemCount = (() => {
        switch (state.kind) {
            case 'empty':
                return emptyFallbackRows.length;
            case 'no-results':
                return noResultsFallbackRows.length;
            case 'loading':
            case 'error':
                return 0;
            case 'results':
                return resolveRenderableSuggestions(state.suggestions, generatePath).length;
        }
    })();

    const { activeIndex, setActiveIndex, itemRef, onKeyDown } = useSearchKeyboard({
        itemCount,
        shouldAutoActivate: state.kind === 'results',
        onEnterSelect: (index) => {
            const target = resolveRowTarget(index);
            if (target) handleSelect(target, 'keyboard', index);
        },
    });

    useFocusTrap(containerRef, true);

    const handleResultClick = useCallback(
        (
            event: MouseEvent<HTMLAnchorElement>,
            suggestion: SearchQuerySuggestion,
            index: number,
        ) => {
            event.preventDefault();
            handleSelect(buildSuggestionTarget(suggestion), 'click', index);
        },
        [buildSuggestionTarget, handleSelect],
    );

    const handleFallbackSelect = useCallback(
        (target: SearchSubmissionTarget, index: number) => handleSelect(target, 'click', index),
        [handleSelect],
    );

    useEffect(() => {
        const handleKeyDown = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') handleClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleClose]);

    const lastTrackedQueryRef = useRef<string>('');
    useEffect(() => {
        if (debouncedQuery.length === 0 || debouncedQuery === lastTrackedQueryRef.current) return;
        lastTrackedQueryRef.current = debouncedQuery;
        trackSearchbarTyped(debouncedQuery);
    }, [debouncedQuery, trackSearchbarTyped]);

    const isDestinationWithRegions =
        state.kind === 'results' && state.type === 'destination_regions';

    const isListboxVisible =
        state.kind === 'results' ||
        state.kind === 'no-results' ||
        (state.kind === 'empty' && Boolean(topDestinations && topDestinations.length > 0));

    const activeOptionId = activeIndex !== null ? getOptionId(activeIndex) : undefined;
    const ariaLiveMessage = buildAriaLiveMessage(formatMessage, state, generatePath);
    const transitionDuration = reducedMotion ? 0.1 : 0.2;

    return createPortal(
        <RemoveScroll removeScrollBar>
            <motion.div
                ref={containerRef}
                className={style.overlay}
                initial={{ opacity: 0, rotateX: reducedMotion ? 0 : 8 }}
                animate={{ opacity: 1, rotateX: 0 }}
                exit={{ opacity: 0, rotateX: reducedMotion ? 0 : 8 }}
                style={{ transformPerspective: 1200, transformOrigin: 'top center' }}
                transition={{ duration: transitionDuration, ease: [0.22, 1, 0.36, 1] }}
            >
                <SearchInput
                    origin={origin}
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onClose={handleClose}
                    onKeyDown={onKeyDown}
                    listboxId={listboxId}
                    isExpanded={isListboxVisible}
                    activeOptionId={activeOptionId}
                />

                <div role="status" aria-live="polite" aria-atomic="true" className={style.srOnly}>
                    {ariaLiveMessage}
                </div>

                <div
                    id={listboxId}
                    role="listbox"
                    aria-label={formatMessage({ id: 'global.search.overlay.listboxLabel' })}
                    aria-busy={state.kind === 'loading'}
                    className={style.body}
                >
                    <SearchResults
                        state={state}
                        emptyFallbackRows={emptyFallbackRows}
                        noResultsFallbackRows={noResultsFallbackRows}
                        isDestinationWithRegions={isDestinationWithRegions}
                        activeIndex={activeIndex}
                        itemRef={itemRef}
                        onMouseEnter={setActiveIndex}
                        getOptionId={getOptionId}
                        onResultClick={handleResultClick}
                        onFallbackSelect={handleFallbackSelect}
                        pendingIndex={pendingIndex}
                    />
                </div>
            </motion.div>
        </RemoveScroll>,
        document.body,
    );
}

type RenderableSuggestion = { suggestion: SearchQuerySuggestion; url: string };

function resolveRenderableSuggestions(
    suggestions: SearchQuerySuggestion[],
    generatePath: ReturnType<typeof useUrlGenerator>['generatePath'],
): RenderableSuggestion[] {
    return suggestions
        .map((suggestion) => ({
            suggestion,
            url: buildSearchSuggestionUrl(suggestion, generatePath),
        }))
        .filter((entry): entry is RenderableSuggestion => entry.url !== null);
}

function buildAriaLiveMessage(
    formatMessage: IntlShape['formatMessage'],
    state: ReturnType<typeof useSearchQuery>,
    generatePath: ReturnType<typeof useUrlGenerator>['generatePath'],
): string {
    if (state.kind === 'no-results') {
        return formatMessage(
            { id: 'global.search.overlay.resultsCount' },
            { count: 0, query: state.query },
        );
    }
    if (state.kind === 'results') {
        const count = resolveRenderableSuggestions(state.suggestions, generatePath).length;
        return formatMessage(
            { id: 'global.search.overlay.resultsCount' },
            { count, query: state.query },
        );
    }
    return '';
}
